import { useRouterState } from '@tanstack/react-router'
import { useEffect, useState } from 'react'

// Lightweight, client-only engagement signals for Intercom audience targeting.
// We keep raw behavior on the device (localStorage) and only ever surface
// aggregate integers to Intercom — never per-tab logs or timestamps to our own
// servers.
//
// Breadth and depth of engagement. The PMF-pulse audience targets on a
// different set — see `use-app-session` and `intercom-founder-attributes`.
//
// Keys are namespaced by user id (not just startup id) so a shared browser
// keeps each account's counts separate — re-logging in as another founder
// reads that founder's totals, never the previous session's.

const TICK_MS = 60_000

const tabsKey = (userId: string, startupId: string) => `sf_tabs_visited_${userId}_${startupId}`
const minutesKey = (userId: string, startupId: string) => `sf_active_minutes_${userId}_${startupId}`

function readTabs(userId: string, startupId: string): string[] {
  try {
    const raw = localStorage.getItem(tabsKey(userId, startupId))
    const parsed = raw ? JSON.parse(raw) : []
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

function readMinutes(userId: string, startupId: string): number {
  const raw = localStorage.getItem(minutesKey(userId, startupId))
  const n = raw ? Number.parseInt(raw, 10) : 0
  return Number.isFinite(n) ? n : 0
}

/** Top-level founder section for the current path, e.g. `outreach`, `profile`. */
function tabKeyFromPath(pathname: string, startupId: string): string | null {
  const base = `/app/startup/${startupId}`
  if (!pathname.startsWith(base)) return null
  const rest = pathname.slice(base.length).replace(/^\//, '')
  return rest === '' ? 'dashboard' : (rest.split('/')[0] ?? null)
}

/**
 * Tracks two cumulative engagement signals per founder, persisted in
 * localStorage: distinct top-level tabs visited, and active (tab-visible)
 * minutes in the app.
 */
export function useFounderEngagement(userId: string, startupId: string) {
  const pathname = useRouterState({ select: (s) => s.location.pathname })
  const [tabsVisited, setTabsVisited] = useState(0)
  const [activeMinutes, setActiveMinutes] = useState(0)

  // Hydrate from storage when the founder (or their startup) changes.
  useEffect(() => {
    setTabsVisited(readTabs(userId, startupId).length)
    setActiveMinutes(readMinutes(userId, startupId))
  }, [userId, startupId])

  // Record each distinct top-level tab the founder lands on.
  useEffect(() => {
    const key = tabKeyFromPath(pathname, startupId)
    if (!key) return
    const tabs = readTabs(userId, startupId)
    if (tabs.includes(key)) return
    const next = [...tabs, key]
    localStorage.setItem(tabsKey(userId, startupId), JSON.stringify(next))
    setTabsVisited(next.length)
  }, [pathname, userId, startupId])

  // Count a minute only while the tab is actually visible — a backstop against
  // counting founders who left the app open in a background tab.
  useEffect(() => {
    const timer = setInterval(() => {
      if (document.visibilityState !== 'visible') return
      const next = readMinutes(userId, startupId) + 1
      localStorage.setItem(minutesKey(userId, startupId), String(next))
      setActiveMinutes(next)
    }, TICK_MS)
    return () => clearInterval(timer)
  }, [userId, startupId])

  return { tabsVisited, activeMinutes }
}
